import React, { useState, useEffect } from 'react';
import { InputNumber, Select, Space } from 'antd';
import PropTypes from 'prop-types';

const TIME_UNITS = [
    { value: 'seconds', label: 'sec', multiplier: 1 },
    { value: 'minutes', label: 'min', multiplier: 60 },
    { value: 'hours', label: 'hr', multiplier: 3600 }
];

const TimeInput = ({ 
    value, 
    onChange, 
    disabled = false, 
    min = 1, 
    max, 
    placeholder = "Enter time",
    style = {},
    defaultUnit = 'seconds',
    allowedUnits = ['seconds', 'minutes', 'hours']
}) => {
    const [timeValue, setTimeValue] = useState(null);
    const [unit, setUnit] = useState(defaultUnit);

    // Convert seconds to display value when component receives a value
    useEffect(() => {
        if (value !== undefined && value !== null) {
            // Find the best unit to display the value
            let bestUnit = 'seconds';
            let bestValue = value;

            // Filter TIME_UNITS to only include allowed units
            const availableUnits = TIME_UNITS.filter(unit => allowedUnits.includes(unit.value));

            // Try to find a unit that gives a clean integer value
            for (const timeUnit of availableUnits.slice().reverse()) { // Start with largest units
                const convertedValue = value / timeUnit.multiplier;
                if (convertedValue >= 1 && convertedValue === Math.floor(convertedValue)) {
                    bestUnit = timeUnit.value;
                    bestValue = convertedValue;
                    break;
                }
            }

            setTimeValue(bestValue);
            setUnit(bestUnit);
        } else {
            setTimeValue(null);
        }
    }, [value]);

    const handleValueChange = (newValue) => {
        setTimeValue(newValue);
        if (newValue !== null && newValue !== undefined) {
            const unitData = TIME_UNITS.find(u => u.value === unit);
            const seconds = newValue * unitData.multiplier;
            
            // Validate against max constraint in seconds
            if (max && seconds > max) {
                // Don't update if it would exceed the max limit
                return;
            }
            
            onChange?.(seconds);
        } else {
            onChange?.(null);
        }
    };

    const handleUnitChange = (newUnit) => {
        setUnit(newUnit);
        if (timeValue !== null && timeValue !== undefined) {
            const unitData = TIME_UNITS.find(u => u.value === newUnit);
            const seconds = timeValue * unitData.multiplier;
            
            // Validate against max constraint in seconds
            if (max && seconds > max) {
                // If changing unit would exceed max, adjust the value to the maximum allowed
                const maxValueForNewUnit = Math.floor(max / unitData.multiplier);
                setTimeValue(maxValueForNewUnit);
                onChange?.(maxValueForNewUnit * unitData.multiplier);
            } else {
                onChange?.(seconds);
            }
        }
    };

    const getCurrentUnitData = () => TIME_UNITS.find(u => u.value === unit);

    const getMinForCurrentUnit = () => {
        const unitData = getCurrentUnitData();
        return Math.ceil(min / unitData.multiplier);
    };

    const getMaxForCurrentUnit = () => {
        if (!max) return undefined;
        const unitData = getCurrentUnitData();
        return Math.floor(max / unitData.multiplier);
    };

    return (
        <Space.Compact style={{ width: '100%', ...style }}>
            <InputNumber
                style={{ flex: 1 }}
                value={timeValue}
                onChange={handleValueChange}
                disabled={disabled}
                min={getMinForCurrentUnit()}
                max={getMaxForCurrentUnit()}
                placeholder={placeholder}
                precision={0}
            />
            <Select
                value={unit}
                onChange={handleUnitChange}
                disabled={disabled}
                style={{ width: 60 }}
                options={TIME_UNITS
                    .filter(unit => allowedUnits.includes(unit.value))
                    .map(unit => ({
                        value: unit.value,
                        label: unit.label
                    }))
                }
            />
        </Space.Compact>
    );
};

TimeInput.propTypes = {
    value: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    placeholder: PropTypes.string,
    style: PropTypes.object,
    defaultUnit: PropTypes.oneOf(['seconds', 'minutes', 'hours']),
    allowedUnits: PropTypes.arrayOf(PropTypes.oneOf(['seconds', 'minutes', 'hours']))
};

export default TimeInput;
